<template>
	<div :class="canEditProject(deliverable.project) ? 'cardWrapper' : ''">
		<button
			v-if="deliverable.emptyCard"
			type="button"
			class="blank-card"
			@click.stop="$emit('add-new-empty')"
		>
			<portal-icon name="clipboard-list" :size="22"></portal-icon>
			<span>{{ $t('projects.add-first-deliverable') }}</span>
		</button>
		<div v-else class="kanban-task-card">
			<div class="task-card-title">{{ deliverable.name }}</div>

			<portal-status-pill v-if="showStatus && status" class="task-card-status">
				<span class="task-status-dot" :style="{ backgroundColor: status.hexColor }"></span>
				{{ status.label }}
			</portal-status-pill>

			<div class="task-card-meta" v-if="hasAssignees || deliverable.dueDate">
				<div class="assignees" v-if="hasAssignees">
					<assigned-user
						v-for="(assignedTo, index) in deliverable.assignedToList"
						:key="assignedTo"
						:show-name="false"
						:assigned-to="assignedTo"
						:assignable-users="assignableUsers"
						:style="{ zIndex: deliverable.assignedToList.length - index }"
					></assigned-user>
				</div>
				<span class="due-date" v-if="deliverable.dueDate">
					<portal-icon name="calendar-days" :size="13"></portal-icon>
					{{ DateTime.fromISO(deliverable.dueDate).toLocaleString({ month: 'short', day: 'numeric' }) }}
				</span>
			</div>

			<div class="task-card-footer">
				<button
					v-if="deliverable.tasks.length"
					type="button"
					class="mini-badge"
					@click.stop="showTasks = !showTasks"
				>
					<portal-icon name="list-checks" :size="13"></portal-icon>
					{{ getTaskCountLabel(deliverable.tasks) }}
					<portal-icon :name="showTasks ? 'chevron-up' : 'chevron-down'" :size="12"></portal-icon>
				</button>
				<button
					v-else-if="!readOnly"
					type="button"
					class="mini-badge"
					:aria-label="$t('projects.add-task')"
					@click.stop="addFirstTask"
				>
					<portal-icon name="list-checks" :size="13"></portal-icon>
					<span aria-hidden="true">+</span>
				</button>
				<span v-else></span>

				<button
					v-if="!deliverable.parentTaskId && deliverable.subtasks.length"
					type="button"
					class="mini-badge"
					@click.stop="showSubtasks = !showSubtasks"
				>
					<portal-icon name="repeat" :size="13"></portal-icon>
					{{ deliverable.subtasks.length }}
					<portal-icon :name="showSubtasks ? 'chevron-up' : 'chevron-down'" :size="12"></portal-icon>
				</button>
			</div>

			<tasks
				ref="tasks"
				v-show="showTasks && deliverable.tasks.length > 0"
				:deliverable="deliverable"
				:disabled="readOnly"
				@empty="showTasks = false"
				@tasks-updated="updateTasks()"
			></tasks>
		</div>
		<div v-if="showSubtasks" class="subtask-tree">
			<div
				v-for="subtask in deliverable.subtasks"
				:key="subtask.id"
				class="subtask-branch"
				@click.stop="$emit('edit-subtask', subtask)"
			>
				<span class="branch-line"></span>
				<kanban-card
					:deliverable="subtask"
					:assignable-users="assignableUsers"
					:status-list="statusList"
					:show-status="true"
				></kanban-card>
			</div>
			<button
				v-if="canEditProject(deliverable.project)"
				type="button"
				class="add-subtask"
				@click.stop="$emit('add-subtask')"
			>
				<span class="branch-line"></span>
				{{ $t('projects.add-task') }}
			</button>
		</div>
	</div>
</template>

<script>
	import { canEditProject } from '@/modules/projects/projectLifecycle';
	import { DateTime as HDateTime } from 'luxon';
	import AssignedUser from '@/components/AssignedUser';
  import Tasks from '@/modules/projects/deliverable/Tasks';
  import ProjectService from '@/modules/projects/ProjectService';

	export default {
		name: 'KanbanCard',

		props: ['deliverable','showStatus','statusList','assignableUsers'],

		components: {
			AssignedUser,
			Tasks,
		},

		data: function() {
			return {
				DateTime: HDateTime,
				showTasks: false,
        showSubtasks: false,
				deliverableService: new ProjectService(),
			};
		},

		mounted() {
			this.$store.state.eventBus.$on('kanban-card-show-tasks', this.setShowTasks);
		},

		beforeDestroy() {
			this.$store.state.eventBus.$off('kanban-card-show-tasks', this.setShowTasks);
		},

		methods: {
			canEditProject,
			expandTasks: function() {
				this.showTasks = true;
			},

			collapseTasks: function() {
				this.showTasks = false;
			},

			getTaskCountLabel: function(tasks) {
				if (tasks.length === 0) {
					return '';
				} else {
					let taskCount = tasks.length;
					let completedCount = 0;
					for (let i = 0; i < tasks.length; i++) {
						if (tasks[i].complete) {
							completedCount++;
						}
					}

					return `${completedCount}/${taskCount}`;
				}
			},

			setShowTasks: function(showTasks) {
				this.showTasks = showTasks;
			},

			addFirstTask: function() {
				this.$refs.tasks.addNewTask(0);
				this.showTasks = true;
			},

			updateTasks() {
				const patch = [{ op: 'replace', path: '/tasks', value: this.deliverable.tasks }];
				this.deliverableService
						.patchDeliverable(this.deliverable.id, patch)
						.catch((err) => {
							console.log(err);
						});
			},
		},

		computed: {
			status: function(){
        return this.statusList.find(s => s.id === this.deliverable.statusId);
			},

			hasAssignees: function() {
				return Boolean(this.deliverable.assignedToList && this.deliverable.assignedToList.length);
			},

      readOnly: function(){
        return !canEditProject(this.deliverable.project);
      },

			highlightColor: function(){
        return '';
			}
		},
	};
</script>

<style scoped lang="scss">
	.cardWrapper {
		cursor: grab;
	}

	.cardWrapper:active {
		cursor: grabbing;
	}

	.kanban-task-card,
	.blank-card {
		width: 100%;
		border: 1px solid var(--portal-border);
		border-radius: var(--brand-surface-radius, 12px);
		background: var(--portal-surface);
		box-sizing: border-box;
	}

	.kanban-task-card {
		padding: 12px;
		box-shadow: 0 1px 2px rgba(17, 24, 39, 0.04);
		text-align: left;
		transition: border-color 140ms ease, box-shadow 140ms ease, transform 140ms ease;
	}

	.kanban-task-card:hover {
		border-color: color-mix(in srgb, var(--v-primary-base) 23%, var(--portal-border));
		box-shadow: 0 8px 20px rgba(17, 24, 39, 0.07);
		transform: translateY(-1px);
	}

	.blank-card {
		min-height: 112px;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		gap: 8px;
		border-style: dashed;
		color: var(--portal-muted);
		font: inherit;
		font-size: 12px;
		font-weight: 650;
		cursor: pointer;
	}

	.blank-card:hover {
		border-color: var(--v-primary-base);
		color: var(--v-primary-base);
	}

	.task-card-title {
		color: var(--portal-text);
		font-size: 14px;
		font-weight: 650;
		line-height: 1.4;
	}

	.task-card-status {
		margin-top: 9px;
	}

	.task-status-dot {
		width: 8px;
		height: 8px;
		flex: 0 0 8px;
		border-radius: 999px;
	}

	.task-card-meta,
	.task-card-footer {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}

	.task-card-meta {
		margin-top: 12px;
	}

	.task-card-footer {
		min-height: 24px;
		margin-top: 10px;
	}

	.assignees {
		display: flex;
		align-items: center;
	}

	.assignees > * + * {
		margin-left: -7px;
	}

	.due-date,
	.mini-badge {
		display: inline-flex;
		align-items: center;
		gap: 5px;
		color: var(--portal-muted);
		font-size: 11px;
		font-weight: 600;
	}

	.mini-badge {
		min-height: 24px;
		padding: 3px 7px;
		border: 0;
		border-radius: 7px;
		background: var(--v-gray_20-base);
		font: inherit;
		cursor: pointer;
	}

	.mini-badge:hover {
		color: var(--portal-text);
	}

	.subtask-tree {
		margin: 8px 0 3px 18px;
		padding-left: 10px;
		border-left: 1px solid var(--portal-border);
	}

	.subtask-branch {
		position: relative;
		display: grid;
		grid-template-columns: 10px minmax(0, 1fr);
		align-items: start;
		margin-top: 8px;
	}

	.branch-line {
		height: 1px;
		margin-top: 20px;
		background: var(--portal-border);
	}

	.add-subtask {
		width: 100%;
		display: grid;
		grid-template-columns: 10px minmax(0, 1fr);
		gap: 6px;
		align-items: center;
		padding: 7px 0;
		border: 0;
		background: transparent;
		color: var(--portal-muted);
		font: inherit;
		font-size: 11px;
		text-align: left;
		cursor: pointer;
	}

	.add-subtask .branch-line {
		margin-top: 0;
	}

	.add-subtask:hover {
		color: var(--v-primary-base);
	}

	@media (prefers-reduced-motion: reduce) {
		.kanban-task-card {
			transition: none;
		}
	}
</style>
